import {useTheme} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import {formatValue} from '../utils/format';
import Highcharts, {BANDO_COLOR} from '../utils/highcharts';
import {PROFILE_AXES, SERIES} from '../utils/labels';
import {CategoryComparison, FragilityComponent} from '../utils/types';

interface ProfileRadarProps {
    data: CategoryComparison<FragilityComponent>;
}

const AXES = Object.keys(PROFILE_AXES) as FragilityComponent[];

const ProfileRadar = ({data}: ProfileRadarProps) => {
    const theme = useTheme();
    const peak = Math.max(0, ...(['project', 'bando'] as const).flatMap((scope) => AXES.map((axis) => data[scope][axis].value ?? 0)));
    const max = Math.min(100, Math.max(20, Math.ceil(peak / 10) * 10));

    const options: Highcharts.Options = {
        chart: {polar: true, height: 360, style: {fontFamily: 'inherit'}, backgroundColor: 'transparent'},
        title: {text: undefined},
        credits: {enabled: false},
        exporting: {enabled: false},
        pane: {size: '78%'},
        xAxis: {
            categories: AXES.map((axis) => PROFILE_AXES[axis]),
            tickmarkPlacement: 'on',
            lineWidth: 0,
            labels: {style: {fontSize: '11px', color: theme.palette.text.secondary, width: 110, textOverflow: 'none'}},
        },
        yAxis: {gridLineInterpolation: 'polygon', min: 0, max, tickAmount: 5, showFirstLabel: false, labels: {format: '{value}%', style: {fontSize: '10px'}}},
        legend: {enabled: true, itemStyle: {fontWeight: '400', color: theme.palette.text.secondary}},
        tooltip: {
            shared: true,
            formatter: function () {
                const axis = AXES[this.points?.[0]?.point.index ?? 0];

                return [
                    `<b>${PROFILE_AXES[axis]}</b>`,
                    ...(['project', 'bando'] as const).map(
                        (scope) => `${SERIES[scope]}: <b>${formatValue(data[scope][axis].value, '%')}</b> (n ${data[scope][axis].n})`,
                    ),
                ].join('<br/>');
            },
        },
        series: (['project', 'bando'] as const).map((scope) => ({
            type: 'line' as const,
            name: SERIES[scope],
            color: scope === 'project' ? theme.palette.primary.main : BANDO_COLOR,
            lineWidth: 2,
            marker: {symbol: 'circle', radius: 3},
            pointPlacement: 'on' as const,
            data: AXES.map((axis) => data[scope][axis].value),
        })),
        responsive: {
            rules: [
                {
                    condition: {maxWidth: 420},
                    chartOptions: {
                        chart: {height: 320},
                        pane: {size: '50%'},
                        xAxis: {labels: {distance: 6, allowOverlap: true, style: {fontSize: '9px', width: 76}}},
                        yAxis: {labels: {enabled: false}},
                    },
                },
            ],
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default ProfileRadar;
